import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, Stack, TextField, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import Logo from "src/components/logo/Logo";
import {
  loginAction,
  setEtsyShops,
  setSubscription,
} from "src/redux/actions/userActions";
import { getAuthUserExtras } from "src/utils/apod";
import { persistCanvaOAuthState } from "src/pages/store/storeOAuth";
import { preloadVipsDashboard } from "src/utils/preloadDashboard";
import { getCatalogsList } from "src/pages/catalog/request";
import { toastify } from "src/utils/toast";
import {
  isVipsUserType,
  normalizeUserType,
  USER_TYPES,
} from "src/utils/userTypes";
import { markVipsAuthPortal, VIPS_DASHBOARD_PATH } from "src/utils/authPortal";
import { loginVip } from "src/pages/vips/request";

const PIN_LENGTH = 4;

const emptyDigits = () => Array.from({ length: PIN_LENGTH }, () => "");

const firstFieldMessage = (value) => {
  if (!value) return "";
  if (Array.isArray(value)) return value.filter(Boolean).join(" ");
  return String(value);
};

const VipLoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pinRefs = useRef([]);

  const [username, setUsername] = useState("");
  const [digits, setDigits] = useState(emptyDigits);
  const [showPin, setShowPin] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const pin = digits.join("");

  const focusPin = (index) => {
    const nextIndex = Math.max(0, Math.min(PIN_LENGTH - 1, index));
    pinRefs.current[nextIndex]?.focus();
  };

  const writeDigits = (startIndex, raw) => {
    const incoming = String(raw || "").replace(/\D/g, "");
    if (!incoming) {
      setDigits((prev) => {
        const next = [...prev];
        next[startIndex] = "";
        return next;
      });
      return;
    }

    setDigits((prev) => {
      const next = [...prev];
      const chars = incoming.slice(0, PIN_LENGTH - startIndex).split("");
      chars.forEach((char, offset) => {
        next[startIndex + offset] = char;
      });
      return next;
    });

    const filledUntil = Math.min(
      PIN_LENGTH - 1,
      startIndex + incoming.length - 1,
    );
    focusPin(filledUntil < PIN_LENGTH - 1 ? filledUntil + 1 : filledUntil);
    if (errors.pin) {
      setErrors((prev) => ({ ...prev, pin: "" }));
    }
  };

  const handlePinChange = (index) => (event) => {
    writeDigits(index, event.target.value);
  };

  const handlePinKeyDown = (index) => (event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      event.preventDefault();
      setDigits((prev) => {
        const next = [...prev];
        next[index - 1] = "";
        return next;
      });
      focusPin(index - 1);
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      focusPin(index - 1);
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      focusPin(index + 1);
    }
  };

  const handlePinPaste = (index) => (event) => {
    const text = event.clipboardData?.getData("text") || "";
    if (!/\d/.test(text)) return;
    event.preventDefault();
    writeDigits(index, text);
  };

  const applyServerErrors = (data) => {
    const serverErrors = data?.errors;
    if (!serverErrors || typeof serverErrors !== "object") return false;

    const nextErrors = {
      username: firstFieldMessage(serverErrors.username || serverErrors.email),
      pin: firstFieldMessage(serverErrors.pin || serverErrors.password),
    };
    if (!Object.values(nextErrors).some(Boolean)) return false;

    setErrors(nextErrors);
    return true;
  };

  const validate = () => {
    const nextErrors = {};
    const trimmedUsername = username.trim();

    if (!trimmedUsername) {
      nextErrors.username = "Enter your username";
    } else if (/\s/.test(trimmedUsername)) {
      nextErrors.username = "Username cannot include spaces";
    }

    if (!/^\d{4}$/.test(pin)) {
      nextErrors.pin = "Enter your 4-digit PIN";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const establishSession = async (body, trimmedUsername) => {
    const data =
      body?.data && typeof body.data === "object" ? body.data : body || {};
    const userData = data.user || data.vip || data;
    const token = data.token || data.access_token || userData?.token;

    if (!token) {
      toastify("error", body?.message || "Sign-in failed. Please try again.");
      return;
    }

    const rawType =
      userData?.UserType || userData?.user_type || userData?.type || "";
    const userType = rawType ? normalizeUserType(rawType) : USER_TYPES.VIPS_ICP;

    if (rawType && !isVipsUserType(rawType)) {
      toastify("error", "This account cannot sign in on the VIP page.");
      return;
    }

    const oauth = data.oauth;
    const expiresIn = data.expiresIn;
    const subscription = data.subscription;
    const etsyShops = userData?.etsy_shops || data.etsy_shops || [];
    const image = userData?.image ?? "";

    localStorage.setItem("token", token);
    localStorage.setItem("oAuth", oauth ?? "");
    localStorage.setItem("expiresIn", expiresIn ?? "");
    localStorage.setItem("etsyStore", etsyShops.length > 0 ? 1 : 0);
    localStorage.setItem("registered", false);
    markVipsAuthPortal();
    persistCanvaOAuthState({ ...data, user: userData });

    dispatch(
      loginAction(
        token,
        userData?.id,
        userData?.name || trimmedUsername,
        userData?.email || trimmedUsername,
        image,
        userType,
        getAuthUserExtras(userData, data),
      ),
    );
    dispatch(setSubscription(subscription));
    if (etsyShops.length > 0) {
      dispatch(setEtsyShops(etsyShops));
    }

    await preloadVipsDashboard();
    getCatalogsList(dispatch);
    navigate(VIPS_DASHBOARD_PATH, { replace: true });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting || !validate()) return;

    const trimmedUsername = username.trim();
    setSubmitting(true);
    try {
      const body = await loginVip({
        username: trimmedUsername,
        pin,
      });
      await establishSession(body, trimmedUsername);
    } catch (error) {
      const data = error?.response?.data;
      const status = error?.response?.status;
      if (!applyServerErrors(data)) {
        const message =
          status === 401
            ? "Invalid username or PIN. Please try again."
            : data?.message || "Sign-in failed. Please try again.";
        toastify("error", message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.50",
        px: 2,
        py: 4,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 420 }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Logo href="/" />
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          autoComplete="off"
          sx={{
            position: "relative",
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            boxShadow: "0 10px 30px rgba(15, 45, 92, 0.06)",
            px: { xs: 2.5, sm: 4 },
            py: { xs: 3, sm: 4 },
          }}
        >
          <Typography variant="h4" color="primary.main" align="center">
            VIP sign in
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 1, mb: 3 }}
          >
            Enter your username and 4-digit PIN.
          </Typography>

          <Stack spacing={2.5}>
            <TextField
              label="Username"
              name="vip-account"
              value={username}
              onChange={(event) => {
                setUsername(event.target.value);
                if (errors.username) {
                  setErrors((prev) => ({ ...prev, username: "" }));
                }
              }}
              error={Boolean(errors.username)}
              helperText={errors.username || " "}
              fullWidth
              disabled={submitting}
              autoComplete="off"
              inputProps={{
                autoComplete: "off",
                autoCorrect: "off",
                autoCapitalize: "none",
                spellCheck: "false",
                maxLength: 80,
                "data-1p-ignore": "true",
                "data-lpignore": "true",
                "data-form-type": "other",
              }}
            />

            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2" color="primary.main">
                  PIN
                </Typography>
                <IconButton
                  aria-label={showPin ? "Hide PIN" : "Show PIN"}
                  onClick={() => setShowPin((prev) => !prev)}
                  size="small"
                  disabled={submitting}
                >
                  {showPin ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </Stack>
              <Stack direction="row" spacing={1.5} justifyContent="center">
                {digits.map((digit, index) => (
                  <Box
                    key={`vip-pin-${index}`}
                    component="input"
                    ref={(node) => {
                      pinRefs.current[index] = node;
                    }}
                    value={digit}
                    onChange={handlePinChange(index)}
                    onKeyDown={handlePinKeyDown(index)}
                    onPaste={handlePinPaste(index)}
                    name={`vip-code-${index}`}
                    disabled={submitting}
                    inputMode="numeric"
                    maxLength={1}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    data-form-type="other"
                    aria-label={`PIN digit ${index + 1}`}
                    aria-invalid={Boolean(errors.pin)}
                    type="text"
                    onFocus={(event) => event.target.select()}
                    sx={{
                      width: 64,
                      height: 64,
                      textAlign: "center",
                      fontSize: 24,
                      fontWeight: 700,
                      color: "primary.main",
                      bgcolor: "background.paper",
                      borderRadius: "12px",
                      border: "1px solid",
                      borderColor: errors.pin ? "error.main" : "divider",
                      WebkitTextSecurity: showPin ? "none" : "disc",
                      "&:focus": {
                        outline: "2px solid",
                        outlineColor: "secondary.main",
                        borderColor: "secondary.main",
                      },
                      "&:disabled": {
                        bgcolor: "grey.50",
                      },
                    }}
                  />
                ))}
              </Stack>
              <Typography
                variant="caption"
                align="center"
                sx={{
                  display: "block",
                  mt: 0.75,
                  minHeight: 20,
                  color: errors.pin ? "error.main" : "transparent",
                }}
              >
                {errors.pin || "PIN"}
              </Typography>
            </Box>
          </Stack>

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={submitting}
            sx={{ mt: 1 }}
          >
            Login
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};

export default VipLoginPage;
